import React, { useRef, useState } from 'react';
import type { BoxProps } from '@interstate/components/Box';
import { Box } from '@interstate/components/Box';
import { Popover } from '@interstate/components/Popover';
import { useResizeObserver } from '@makemydeal/dr-shared-ui-utils';
import { TEST_IDS } from '../../../constants';
import { TemplateCombinationPopover } from './TemplateOptionsPopover';

type TemplateNameProps = {
    id: string;
    name: string;
    description?: string;
    details?: any;
};

export const getTextBoxSx = (isTextTruncated = false) => ({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 245,
    ...(isTextTruncated && { cursor: 'pointer' })
});

const getPopoverWrapperSx = ({ shouldShowPopover }: { shouldShowPopover: boolean }): BoxProps['sx'] => {
    return {
        display: 'flex',

        '& .ids-popover-anchor-container': {
            ...(!shouldShowPopover && { pointerEvents: 'none' })
        }
    };
};

export const isTruncated = (element: Element | null) => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
};

export const TemplateName = ({ name, id, description, details }: TemplateNameProps) => {
    const templateNameNode = useRef<HTMLElement>(null);
    const templateDescriptionNode = useRef<HTMLElement>(null);
    const [isTemplateNameTruncated, setIsTemplateNameTruncated] = useState(false);
    const [isTemplateDescriptionTruncated, setIsTemplateDescriptionTruncated] = useState(false);

    useResizeObserver(templateNameNode, (entry) => {
        setIsTemplateNameTruncated(isTruncated(entry.target));
    });

    useResizeObserver(templateDescriptionNode, (entry) => {
        setIsTemplateDescriptionTruncated(isTruncated(entry.target));
    });

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box sx={getPopoverWrapperSx({ shouldShowPopover: isTemplateNameTruncated })}>
                    <Popover
                        data-testid={TEST_IDS.TEMPLATE_NAME_POPOVER}
                        position="bottom"
                        trigger="outsideClick"
                        popoverContent={name}
                    >
                        <Box
                            ref={templateNameNode}
                            data-testid={TEST_IDS.TEMPLATE_NAME(id)}
                            sx={getTextBoxSx(isTemplateNameTruncated)}
                        >
                            <strong>{name}</strong>
                        </Box>
                    </Popover>
                </Box>
                {details && <TemplateCombinationPopover details={details} />}
            </Box>
            {description && (
                <Box sx={getPopoverWrapperSx({ shouldShowPopover: isTemplateDescriptionTruncated })}>
                    <Popover
                        data-testid={TEST_IDS.TEMPLATE_DESCRIPTION_POPOVER}
                        position="bottom"
                        trigger="outsideClick"
                        popoverContent={description}
                    >
                        <Box
                            ref={templateDescriptionNode}
                            data-testid={TEST_IDS.TEMPLATE_DESCRIPTION(id)}
                            sx={getTextBoxSx(isTemplateDescriptionTruncated)}
                        >
                            {description}
                        </Box>
                    </Popover>
                </Box>
            )}
        </>
    );
};
